import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { familiesService } from '../services/api';

const FamilyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewFamily = !id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '',
    icon: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!isNewFamily) {
      fetchFamily();
    }
  }, [id]);

  const fetchFamily = async () => {
    try {
      setLoading(true);
      const response = await familiesService.getById(id);
      const family = response.data;
      setFormData({
        name: family.name || '',
        description: family.description || '',
        color: family.color || '',
        icon: family.icon || '',
        isActive: family.isActive,
      });
    } catch (err) {
      setError('Erreur lors du chargement de la famille');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isNewFamily) {
        await familiesService.create(formData);
        setSuccess('Famille créée avec succès');
      } else {
        await familiesService.update(id, formData);
        setSuccess('Famille mise à jour avec succès');
      }
      
      setTimeout(() => {
        navigate('/families');
      }, 1500);
    } catch (err) {
      setError(isNewFamily ? 'Erreur lors de la création' : 'Erreur lors de la mise à jour');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const predefinedColors = [
    '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
    '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50',
    '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800',
    '#FF5722', '#795548', '#9E9E9E', '#607D8B'
  ];

  const predefinedIcons = [
    '🖌️', '🎨', '⚡', '🔋', '💉', '🧴', '🩹', '💊',
    '🔬', '🧪', '💎', '⭐', '🌟', '✨', '💫', '🔥',
    '❄️', '🌈', '🎯', '🔍', '📦', '📋', '🏷️', '🔖'
  ];

  if (loading && !isNewFamily) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/families')}
        >
          Familles
        </Link>
        <Typography color="text.primary">
          {isNewFamily ? 'Nouvelle Famille' : 'Modifier la Famille'}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {isNewFamily ? 'Nouvelle Famille de Produit' : 'Modifier la Famille'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom de la famille"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                helperText="Ex: Encre, Aiguilles, Avant tattoo..."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                  />
                }
                label="Famille active"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                helperText="Description de la famille (optionnel)"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Couleur
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {predefinedColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: color,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      border: formData.color === color ? '3px solid #000' : '1px solid #ddd',
                      '&:hover': {
                        border: '2px solid #000',
                      },
                    }}
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                label="Code couleur personnalisé"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                placeholder="#FF5722"
                helperText="Code hexadécimal (ex: #FF5722)"
              />
              {formData.color && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: formData.color,
                      borderRadius: '50%',
                      border: '1px solid #ddd',
                    }}
                  />
                  <Typography variant="body2">Aperçu</Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Icône
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {predefinedIcons.map((icon) => (
                  <Chip
                    key={icon}
                    label={icon}
                    onClick={() => setFormData(prev => ({ ...prev, icon }))}
                    variant={formData.icon === icon ? 'filled' : 'outlined'}
                    sx={{
                      fontSize: '1.2rem',
                      height: 40,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                label="Icône personnalisée"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
                placeholder="🖌️"
                helperText="Emoji ou caractère spécial"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    isNewFamily ? 'Créer' : 'Mettre à jour'
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/families')}
                >
                  Annuler
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default FamilyEdit;




















